import {
  CENTRAL_AUTH_PAGE_PATHS,
  COMPANY_SIGN_IN_CONSENT_PATH,
  COMPANY_SIGN_IN_TEST_PATH,
  FEDERATED_SIGNUP_PATH,
} from '@instanexus/contracts';
import { createClient } from '@instanexus/sdk';
import { ThemeProvider } from '@instanexus/ui';
import { getAccessToken } from '@instanexus/web-auth';
import { QueryClientProvider } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { createBrowserRouter, RouterProvider, type RouteObject } from 'react-router';

import { ErrorBoundary } from '@/components/error-boundary';
import { RedirectToLogin } from '@/components/redirect-to-login';
import { RouteErrorFallback } from '@/components/route-error-fallback';
import { ApiClientContext } from '@/lib/api-client-context';
import { initI18n } from '@/lib/i18n';
import { createQueryClient } from '@/lib/query-client';
import { AcceptInvitePage } from '@/pages/accept-invite-page';
import { CompanySignInConsentPage } from '@/pages/company-sign-in-consent-page';
import { CompanySignInTestPage } from '@/pages/company-sign-in-test-page';
import { FederatedCallbackPage } from '@/pages/federated-callback-page';
import { FederatedSignupPage } from '@/pages/federated-signup-page';
import { ForgotPasswordPage } from '@/pages/forgot-password-page';
import { ResetPasswordPage } from '@/pages/reset-password-page';
import { SignInMethodsPage } from '@/pages/sign-in-methods-page';
import { SignInPage } from '@/pages/sign-in-page';
import { SignOutPage } from '@/pages/sign-out-page';
import { SignupPage } from '@/pages/signup-page';
import { TermsPage } from '@/pages/terms-page';
import { VerifyEmailPage } from '@/pages/verify-email-page';
import { VerifyPhonePage } from '@/pages/verify-phone-page';

/**
 * Mirrors `base` in vite.config.ts. Each console is served from the root of its
 * own dedicated distribution and subdomain (docs/013) — no path prefix.
 */
const ROUTER_BASENAME = '/';

initI18n();

const queryClient = createQueryClient();

// The central session cookie is the credential; the short-lived access token from it is held in
// memory only (the shared store) to read `me` and record Terms acceptance.
const apiClient = createClient({ baseUrl: window.location.origin, getAuthToken: getAccessToken });

const stripLeadingSlash = (path: string): string => path.replace(/^\//, '');

// A render crash anywhere in a routed page lands in `errorElement`, so the pathless root carries it.
// Exported so a test can mount the same table on a memory router.
export const appRoutes: RouteObject[] = [
  {
    errorElement: <RouteErrorFallback />,
    children: [
      { path: 'login', element: <SignInPage /> },
      {
        path: stripLeadingSlash(CENTRAL_AUTH_PAGE_PATHS.forgotPassword),
        element: <ForgotPasswordPage />,
      },
      {
        path: stripLeadingSlash(CENTRAL_AUTH_PAGE_PATHS.resetPassword),
        element: <ResetPasswordPage />,
      },
      { path: stripLeadingSlash(CENTRAL_AUTH_PAGE_PATHS.signup), element: <SignupPage /> },
      { path: stripLeadingSlash(FEDERATED_SIGNUP_PATH), element: <FederatedSignupPage /> },
      {
        path: stripLeadingSlash(CENTRAL_AUTH_PAGE_PATHS.verifyEmail),
        element: <VerifyEmailPage />,
      },
      {
        path: stripLeadingSlash(CENTRAL_AUTH_PAGE_PATHS.verifyPhone),
        element: <VerifyPhonePage />,
      },
      {
        path: stripLeadingSlash(CENTRAL_AUTH_PAGE_PATHS.acceptInvite),
        element: <AcceptInvitePage />,
      },
      { path: stripLeadingSlash(CENTRAL_AUTH_PAGE_PATHS.terms), element: <TermsPage /> },
      { path: 'auth/:provider/callback', element: <FederatedCallbackPage /> },
      { path: 'sign-in-methods', element: <SignInMethodsPage /> },
      {
        path: stripLeadingSlash(COMPANY_SIGN_IN_TEST_PATH),
        element: <CompanySignInTestPage />,
      },
      {
        path: stripLeadingSlash(COMPANY_SIGN_IN_CONSENT_PATH),
        element: <CompanySignInConsentPage />,
      },
      { path: 'logout', element: <SignOutPage /> },
      { path: '*', element: <RedirectToLogin /> },
    ],
  },
];

const router = createBrowserRouter(appRoutes, { basename: ROUTER_BASENAME });

export function App(): ReactElement {
  return (
    // Pinned, not read from storage: these screens have no appearance control, so a stored
    // choice could never be undone here. The first thing anyone sees is the default brand.
    <ThemeProvider initialMode="light" initialPalette="instacheckin">
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ApiClientContext.Provider value={apiClient}>
            <RouterProvider router={router} />
          </ApiClientContext.Provider>
        </QueryClientProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}
